import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Stack, Card, Title, Button, Text, Group, Badge } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useBots, botName } from '../../../lib/bots';
import { BotEditor } from './components/BotEditor';
import { AddBotModal } from './components/AddBotModal';
import { useT } from '../../../i18n/I18nProvider';
import './assistant.css';

/**
 * Settings → Assistant.
 *
 * With one chatbot this is that chatbot's editor; with two or more (21
 * September 2026) it is the list of them, each opening its own editor, which is
 * also where a chatbot is told which numbers, website chats and accounts it
 * answers on.
 *
 * Either way the page opens on "Your chatbots", how many the plan allows and
 * the button to add one. The way to add a second chatbot used to sit under the
 * editor, three screens down, and the owner looked at this page on production
 * and concluded there was no such thing (22 September 2026).
 */
export default function SettingsAssistantPage() {
  const t = useT();
  const nav = useNavigate();
  const { bots, multi, canAdd, allowance, isPending, error } = useBots();
  const [adding, setAdding] = useState(false);
  const fallback = t('settings.assistant.defaultName');

  if (isPending) return <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>;
  if (error || bots.length === 0) return <Alert color="error">{t('bots.loadError')}</Alert>;

  const count = !allowance
    ? null
    : allowance.unlimited || allowance.allowance == null
      ? t('bots.header.countUnlimited', { current: bots.length, plan: allowance.plan_name ?? '' })
      : t('bots.header.count', { current: bots.length, allowance: allowance.allowance, plan: allowance.plan_name ?? '' });

  const header = (
    <Card withBorder>
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Stack gap={4} style={{ flex: 1, minWidth: 240 }}>
          <Group gap="xs" align="center">
            <Title order={4}>{t('bots.list.title')}</Title>
            {count && <Badge size="sm" variant="light">{count}</Badge>}
          </Group>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">
            {!canAdd
              ? t('bots.add.atLimit', { plan: allowance?.plan_name ?? '', count: allowance?.allowance ?? bots.length })
              : multi
                ? t('bots.list.intro')
                : t('bots.header.single', { name: botName(bots[0], fallback) })}
            {!canAdd && <> <Link to="/billing">{t('bots.add.seePlans')}</Link></>}
          </Text>
        </Stack>
        {canAdd && <Button size="sm" onClick={() => setAdding(true)}>{t('bots.add.action')}</Button>}
      </Group>
    </Card>
  );

  return (
    <Stack gap="lg">
      {header}
      {multi ? (
        <>
          <div className="rr-bots">
            {bots.map((b) => (
              <button key={b.id} type="button" className="rr-bots__card" onClick={() => nav(`/settings/assistant/${b.id}`)}>
                <Group gap="sm" align="center" wrap="nowrap">
                  <Text role="subtitle" fw={700} truncate>{botName(b, fallback)}</Text>
                  {b.is_default && <Badge size="xs" variant="light" color="blue">{t('bots.list.default')}</Badge>}
                </Group>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">
                  {b.is_default
                    ? t('bots.list.answersDefault', { count: b.channels.length })
                    : t('bots.list.answers', { count: b.channels.length })}
                </Text>
                <Text role="caption" c="var(--rr-color-text-muted)">{t('bots.list.flows', { count: b.flow_count })}</Text>
              </button>
            ))}
          </div>
        </>
      ) : (
        <BotEditor key={bots[0].id} bot={bots[0]} multi={false} />
      )}
      {adding && <AddBotModal opened={adding} onClose={() => setAdding(false)} />}
    </Stack>
  );
}
